import { useEffect, useRef, useState, useCallback } from 'react';
import {
    getSessionTimeoutMinutes,
    getSessionWarningMinutes
} from '../utils/runtimeConfig';

import { useAuth } from '../auth/AuthContext';
import { stopSignalR } from '../../src/services/signalrService';

const ACTIVITY_EVENTS = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click'
];

const THROTTLE_MS = 1000;

export const useSessionTimeout = ({ onLogout, enabled = true }) => {
    const [showWarning, setShowWarning] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(0);

    const { refreshAccessToken, isImpersonating,user } = useAuth();

    const sessionExpiryRef = useRef(null);

    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const throttleRef = useRef(0);

    /* ---------------- CLEAR TIMERS ---------------- */
    const clearAllTimers = useCallback(() => {
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

        warningTimerRef.current = null;
        logoutTimerRef.current = null;
        countdownIntervalRef.current = null;
    }, []);

    /* ---------------- SCHEDULE BASED ON FIXED EXPIRY ---------------- */
    const scheduleTimers = useCallback(() => {
        clearAllTimers();

        const expiryAt =
            sessionExpiryRef.current ||
            Number(localStorage.getItem('session_expiry'));

        if (!expiryAt) return;

        const warningMs =
            getSessionWarningMinutes() * 60 * 1000;

        const warningAt = expiryAt - warningMs;

        /* ---------- WARNING TIMER ---------- */
        warningTimerRef.current = setTimeout(() => {
            setShowWarning(true);

            countdownIntervalRef.current = setInterval(() => {
                const remaining = Math.max(
                    0,
                    Math.ceil((expiryAt - Date.now()) / 1000)
                );

                setSecondsRemaining(remaining);

                if (remaining <= 0) {
                    clearInterval(countdownIntervalRef.current);
                    handleLogout();
                }
            }, 1000);

        }, Math.max(warningAt - Date.now(), 0));

        /* ---------- FINAL LOGOUT TIMER ---------- */
        logoutTimerRef.current = setTimeout(() => {
            handleLogout();
        }, Math.max(expiryAt - Date.now(), 0));

    }, [clearAllTimers]);

    /* ---------------- NO RESET ON ACTIVITY ---------------- */
    const recordActivity = useCallback(() => {
        const now = Date.now();

        if (now - throttleRef.current < THROTTLE_MS) return;

        throttleRef.current = now;

        // intentionally EMPTY
        // session does NOT extend on activity anymore
    }, []);

    /* ---------------- EXTEND SESSION ---------------- */
    const extendSession = useCallback(async () => {
       // alert("isImpersonating : " + isImpersonating);

        let refreshToken;
        if (isImpersonating)
        {
            if (user?.userRole?.trim().toLowerCase() === "super admin" || user?.userRole?.trim().toLowerCase() === "admin")
            {
              //  alert("isImpersonating IN SUPER ADMIN : " + localStorage.getItem('sendero_original_refresh_token'));
                refreshToken = localStorage.getItem('sendero_original_refresh_token');
            }
            else
            {
                refreshToken = localStorage.getItem('sendero_refresh_token');  
             //   alert("isImpersonating IN OTHERS USERS : " + localStorage.getItem('sendero_refresh_token'));
            }
            
        }
        else
        {
           // alert("isImpersonating ELSE : " + localStorage.getItem('sendero_refresh_token'));
            refreshToken = localStorage.getItem('sendero_refresh_token');  
            
        }

        
        console.log("HIT RF1");  

        const success = await refreshAccessToken(refreshToken, isImpersonating);        

        console.log("------------------------------------------");
        console.log("refreshAccessToken at useSessionTimeout : "+JSON.stringify(success));
        console.log("------------------------------------------");
        console.log("HIT RF3");  
        if (!success) {
            console.log("RESULT FAILED");
            handleLogout();
            return;
        }

        const newExpiry =
            Date.now() + getSessionTimeoutMinutes() * 60 * 1000;

        sessionExpiryRef.current = newExpiry;
        localStorage.setItem('session_expiry', newExpiry);

        setShowWarning(false);
        setSecondsRemaining(0);

        scheduleTimers();

    }, [refreshAccessToken, scheduleTimers]);

    /* ---------------- LOGOUT ---------------- */
    const handleLogout = useCallback(() => {
        clearAllTimers();
        setShowWarning(false);

        //STOP SignalR Connection
        stopSignalR();

        if (onLogout) onLogout('timeout');
    }, [clearAllTimers, onLogout]);

    /* ---------------- INIT ---------------- */
    useEffect(() => {
        if (!enabled) {
            clearAllTimers();
            setShowWarning(false);
            return;
        }

        const timeoutMs =
            getSessionTimeoutMinutes() * 60 * 1000;

        const expiryAt = Date.now() + timeoutMs;

        sessionExpiryRef.current = expiryAt;
        localStorage.setItem('session_expiry', expiryAt);

        scheduleTimers();

        ACTIVITY_EVENTS.forEach(evt => {
            window.addEventListener(evt, recordActivity, { passive: true });
        });

        return () => {
            clearAllTimers();

            ACTIVITY_EVENTS.forEach(evt => {
                window.removeEventListener(evt, recordActivity);
            });
        };
    }, [enabled, scheduleTimers, recordActivity, clearAllTimers]);

    return {
        showWarning,
        secondsRemaining,
        extendSession,
        handleLogout,
    };
};